import { IConfigRetailUser, IConfigStateGroups, Result, Subtask } from 'constants/interfaces'
import keys from 'constants/keys'
import dayjs from 'dayjs'
import { DeepDiveInsights } from 'redux/actions/tasks_v2'
import { isExternalFormLink } from 'utils/jotformLink'
import { Content } from '../TaskCreation/FormStepContext'
import { PRIORITY_OPTIONS } from '../TaskCreation/utils'
import { CHART_COLORS, TASK_STATUS_CONFIG } from 'utils/taskAnalytics'

interface IBuildPollOptions {
  options: (string | { value?: string })[]
  pollResults: Result[]
  permittedUsers: string[]
  permittedGroups: IConfigStateGroups
  deepDiveInsights?: DeepDiveInsights
  groups: IConfigStateGroups
  retailUsersObject: { [key: string]: IConfigRetailUser }
}
export const buildPollOptions = ({
  options,
  pollResults,
  permittedUsers,
  permittedGroups,
  deepDiveInsights,
  groups,
  retailUsersObject,
}: IBuildPollOptions) => {
  return Object.entries(options).map(([key, label]) => {
    const normalizedLabel = typeof label === 'string' ? label : label.value ?? ''
    const currentQuestionResults = pollResults.filter(
      (result) =>
        result?.poll_selections?.includes(parseInt(key, 10)) &&
        (Object.keys(retailUsersObject).includes(result.context_id) || Object.keys(groups).includes(result.context_id))
    )

    const count = currentQuestionResults.length

    const currentQuestionParticipants = currentQuestionResults.map((result) => {
      return {
        id: result.context_id,
        date: result.updated_at_ts ? dayjs.unix(result.updated_at_ts).toISOString() : '',
        isClickable:
          permittedUsers.includes(result.context_id) || Object.keys(permittedGroups).includes(result.context_id),
        ...(deepDiveInsights ? { insights: deepDiveInsights?.insights?.[result.context_id] } : {}),
      }
    })

    return { label: normalizedLabel, count, currentQuestionParticipants }
  })
}

// Canonical gate lives in utils/jotformLink — kept re-exported under this name since it's already imported
// as `isExternalLink` elsewhere in Tasks (e.g. the task-creation Link widget).
export const isExternalLink = isExternalFormLink

export { getFormattedLink } from './model/link'

export const sortContentAndSubtasks = (subtasks: Subtask[] = [], contents: Content[] = []) => {
  const subtasksList = [...subtasks].sort((a, b) => a.order_index - b.order_index)

  if (!contents || contents.length === 0) {
    return subtasksList
  }

  // Сортируем контент по index_ref
  const sortedContents = [...contents].sort((a, b) => a.index_ref - b.index_ref)
  const result: (Content | Subtask)[] = []
  let subtaskIndex = 0

  // Добавляем контент с index_ref = 0 в начало, если есть
  const firstContent = sortedContents.find((content) => content.index_ref === 0)
  if (firstContent) {
    result.push(firstContent)
  }

  // Проходим по оставшимся контентам
  for (let i = 0; i < sortedContents.length; i++) {
    const currentContent = sortedContents[i]
    if (currentContent.index_ref === 0) continue // Пропускаем, если уже добавили

    // Добавляем сабтаски между контентами
    while (subtaskIndex < subtasksList.length && result.length < currentContent.index_ref) {
      result.push(subtasksList[subtaskIndex])
      subtaskIndex++
    }

    result.push(currentContent)
  }

  // Добавляем оставшиеся сабтаски
  while (subtaskIndex < subtasksList.length) {
    result.push(subtasksList[subtaskIndex])
    subtaskIndex++
  }

  return result
}

export const getPriority = (priority: number) => {
  const foundPriority = PRIORITY_OPTIONS.find((priorityOption) => {
    const priorityValue = priorityOption.value
    return priorityValue === priority
  })
  return foundPriority
}

export const getStatusCircleColor = (status: number) => {
  switch (status) {
    case keys.TASK_STATUSES.DONE.value:
      return CHART_COLORS.doneText
    case keys.TASK_STATUSES.SKIPPED.value:
      // Skipped has its own color in the status config (its own literal too — "Not applicable").
      return TASK_STATUS_CONFIG.skipped.bar
    case keys.TASK_STATUSES.INPROGRESS.value:
      return CHART_COLORS.inProgressText
    case keys.TASK_STATUSES.APPROVED.value:
      return CHART_COLORS.approvedText
    case keys.TASK_STATUSES.APPROVED_WITH_RESERVATION.value:
      return TASK_STATUS_CONFIG.approvedWithReservation.bar
    case keys.TASK_STATUSES.DISAPPROVED.value:
      return CHART_COLORS.rejectedText
    case keys.TASK_STATUSES.REVIEWED_BY_AI.value:
      return CHART_COLORS.reviewedByAiText
    default:
      return CHART_COLORS.todoText
  }
}

export { parseRecurrenceTimestamp, passesRecurrenceFilter } from './model/results'
